/**
 * React Query hooks for the Server Billing screen.
 *
 * Cadences mirror the legacy dashboard hook (useServerBillingQuery.js):
 *   - overview:  refetchInterval 60s  — resources/status drift slowly
 *   - forecast:  refetchInterval 300s — recomputed from balance + invoices
 *   - invoices:  staleTime 300s, no poll — billing history is near-static
 *   - invoice detail: enabled-on-demand (only when a row is expanded)
 *   - metrics:   enabled-on-demand + refetchInterval 300s while open
 */
import { useQuery } from '@tanstack/react-query';
import {
  fetchServerForecast,
  fetchServerInvoiceDetail,
  fetchServerInvoices,
  fetchServerMetrics,
  fetchServerOverview
} from '@/api/serverBilling';

export function useServerOverview() {
  return useQuery({
    queryKey: ['serverOverview'],
    queryFn: fetchServerOverview,
    refetchInterval: 60_000
  });
}

export function useServerForecast() {
  return useQuery({
    queryKey: ['serverForecast'],
    queryFn: fetchServerForecast,
    refetchInterval: 300_000
  });
}

export function useServerInvoices() {
  return useQuery({
    queryKey: ['serverInvoices'],
    queryFn: fetchServerInvoices,
    staleTime: 300_000
  });
}

export function useServerInvoiceDetail(uuid: string | null) {
  return useQuery({
    queryKey: ['serverInvoice', uuid],
    queryFn: () => fetchServerInvoiceDetail(uuid as string),
    enabled: !!uuid
  });
}

export function useServerMetrics(dropletId: string | null) {
  return useQuery({
    queryKey: ['serverMetrics', dropletId],
    queryFn: () => fetchServerMetrics(dropletId as string),
    enabled: !!dropletId,
    refetchInterval: 300_000
  });
}
